import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { getTranslatedStoreValue } from '@/components/StoresList/utils';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { usePosition } from '@/hooks/usePosition';
import type { StoreHit } from '@/integrations/algolia/types';
import {
  getGoogleMapAddress,
  getTimeWithOffset,
  isWithinOpenHours,
  transformToFormat,
} from '@/lib/boutique/helpers';
import { EVENT_NAME } from '@/lib/gtm/const';
import { getGeoDistance } from '@/lib/maps';
import { paths } from '@/lib/paths';
import { commonMessages } from '@/messages/appointment';
import { messages } from '@/messages/boutique';
import { commonMessages as commonMsgs } from '@/messages/common';
import { formMessages } from '@/messages/forms';
import { useRegionProvider } from '@/providers/RegionProvider';
import { theme } from '@/theme';

import { ReactComponent as BoutiqueSvg } from '@/assets/icons/boutique-icon.svg';

import * as S from './BoutiqueCard.styles';

interface BoutiqueCardProps {
  boutique: StoreHit;
  countrySlug?: string;
}
export const BoutiqueCard = ({ boutique, countrySlug }: BoutiqueCardProps) => {
  const [isOpenColor, setIsOpenColor] = useState<string>(theme.colors.error);
  const [openTime, setOpenTime] = useState<string>('');
  const [openText, setOpenText] = useState<string>('');
  const [distance, setDistance] = useState<number>();
  const { formatMessage } = useIntl();
  const { position } = usePosition();
  const { sendEvent } = useGoogleAnalytics();
  const { locale } = useRegionProvider();
  const langSlug = locale.slug;

  const {
    _geoloc,
    emails,
    hours,
    offers_appointment,
    phone,
    photo_gallery,
    slug,
    type,
    utc_offset,
  } = boutique;
  const transferedType = type?.toLowerCase()?.includes('boutique')
    ? formatMessage(messages.boutique)
    : '';
  useEffect(() => {
    if (
      _geoloc?.lat &&
      position?.coords?.latitude &&
      position?.coords?.longitude
    ) {
      const currentPosition = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };
      const geoDistance = getGeoDistance(currentPosition, _geoloc);
      setDistance(geoDistance);
    }
  }, [_geoloc?.lat, position?.coords?.latitude]);
  useEffect(() => {
    if (hours) {
      const formattedHours = transformToFormat(hours);
      const localTimeInMinutes = getTimeWithOffset(utc_offset);
      const todayWeekday = localTimeInMinutes.weekday;
      const todayHours = formattedHours.find((day) => day.val === todayWeekday);
      const withinOrNot = isWithinOpenHours(localTimeInMinutes, formattedHours);

      if (withinOrNot) {
        todayHours?.time && setOpenTime(todayHours.time);
        setOpenText(formatMessage(messages.openNow));
        setIsOpenColor(theme.colors.primary);
      } else {
        for (const formattedHour of formattedHours) {
          if (formattedHour?.time) {
            setOpenTime(
              `${formatMessage(
                messages.startBusiness,
              )} ${formattedHour.time?.substring(0, 5)} on ${
                formattedHour.val
              }`,
            );
            break;
          }
        }
        setOpenText(formatMessage(messages.closed));
        setIsOpenColor(theme.colors.error);
      }
    } else {
      setOpenTime('');
      setOpenText(formatMessage(messages.temporaryClosed));
      setIsOpenColor(theme.colors.secondaryGrey);
    }
  }, [hours]);
  const directToBoutiqueUrl =
    countrySlug && slug
      ? paths.stores.breitlingBoutiques.asPath({
          country: countrySlug,
          slug: slug,
        })
      : '';

  const directToAppointmentUrl =
    countrySlug && slug
      ? paths.stores.appointment.asPath({
          country: countrySlug,
          slug: slug,
        })
      : '';
  const handleDirectToAppointmentEvent = () => {
    if (countrySlug && slug) {
      sendEvent({
        event: EVENT_NAME.INTENTION_BOOK_APPT,
      });
    }
  };
  const emailHref = `mailto:${emails?.[0]}`;

  const nameText = () => {
    return `${getTranslatedStoreValue(boutique, 'name', langSlug)}`;
  };
  const addressText = () => {
    return `${getTranslatedStoreValue(boutique, 'street', langSlug)}`;
  };
  const street2Text = () => {
    return getTranslatedStoreValue(boutique, 'street2', langSlug);
  };
  return (
    <S.BoutiqueCardWrapper>
      <S.BoutiqueCardContent>
        <S.BoutiqueImageWrapper href={directToBoutiqueUrl}>
          <S.StyledBoutiqueImage
            alt='boutique image'
            aspectRatio={{ x: 16, y: 9 }}
            focalPoint={{ x: 960, y: 262 }}
            loaderProps={{ height: 240 }}
            loading='eager'
            originalImageDimensions={undefined}
            showFallbackPlaceholder
            sizes='(max-width: 600px) 50vw, 20vw'
            src={photo_gallery?.[0]?.image?.url}
            useLoader
            width='100%'
          />
        </S.BoutiqueImageWrapper>
        <S.InfoWrapper>
          <S.NameAndTypeWrapper>
            <BoutiqueSvg height={35} width={40} />
            <S.NameAndType>
              <S.NameAndDistanceWrapper>
                <S.Name>{nameText()}</S.Name>
                {distance && (
                  <S.DistanceWrapper data-test-id='DistanceWrapper'>
                    <S.StyledOtherDirectionSvg height={10} width={10} />
                    <S.StyledDistance data-test-id='StyledDistance'>
                      {distance + ' km'}
                    </S.StyledDistance>
                  </S.DistanceWrapper>
                )}
              </S.NameAndDistanceWrapper>

              <S.Type data-testid='Type'>
                <S.TypeText>{transferedType}</S.TypeText>
              </S.Type>
            </S.NameAndType>
          </S.NameAndTypeWrapper>
          <S.OpenHoursWrapper>
            <S.OpenOrClose>
              <S.StyledSpan color={isOpenColor} />
              {openText}
            </S.OpenOrClose>
            <S.Opens>{openTime}</S.Opens>
          </S.OpenHoursWrapper>
          <S.AddressPhoneEmailWrapper data-test-id='AddressPhoneEmailWrapper'>
            <S.StreetSpan>{addressText()?.toLowerCase()}</S.StreetSpan>
            {street2Text() && (
              <S.StreetSpan>{street2Text()?.toLowerCase()}</S.StreetSpan>
            )}
            <div>
              {formatMessage(messages.Phone)}
              {': '}
              <S.StyledLink data-testid='phone' href={`tel:${phone}`}>
                {phone}
              </S.StyledLink>
            </div>
            <div>
              {formatMessage(formMessages.email)}
              {': '}
              <S.StyledLink data-testid='email' href={emailHref}>
                {formatMessage(messages.sendAnEmail)}
              </S.StyledLink>
            </div>
          </S.AddressPhoneEmailWrapper>
          <S.CtaWrapper>
            <S.Button
              colorVariant='blue'
              href={getGoogleMapAddress(_geoloc)}
              isUpperCase={false}
              label={formatMessage(messages.getDirections)}
              lower
              onClick={() =>
                sendEvent({
                  event: EVENT_NAME.LOCATOR_PIN_DETAILS,
                  retailer_name: boutique.name,
                })
              }
              rel='noopener noreferrer'
              target='_blank'
              underline
              variant='link'
            >
              <S.Chevron height={24} />
            </S.Button>
            <S.Button
              colorVariant='blue'
              data-testid='viewMore'
              href={directToBoutiqueUrl}
              isUpperCase={false}
              label={formatMessage(messages.viewMore)}
              lower
              underline
              variant='link'
            >
              <S.Chevron height={24} />
            </S.Button>
          </S.CtaWrapper>
          <S.BookAppointmentCtaWrapper data-test-id='BookAppointmentCtaWrapper'>
            {offers_appointment ? (
              <S.Button
                colorVariant='primary'
                data-testid='offersAppointment'
                href={directToAppointmentUrl}
                isFluid
                isUpperCase
                label={formatMessage(commonMsgs.bookAppointment)}
                onClick={handleDirectToAppointmentEvent}
                variant='filled'
              />
            ) : (
              <S.Button
                colorVariant='primary'
                data-test-id='ViewBoutiqueDetail'
                href={directToBoutiqueUrl}
                isFluid
                isUpperCase
                label={formatMessage(commonMessages.viewBoutiqueDetail)}
                variant='filled'
              />
            )}
          </S.BookAppointmentCtaWrapper>
        </S.InfoWrapper>
      </S.BoutiqueCardContent>
    </S.BoutiqueCardWrapper>
  );
};
